// ONE carrier-status vocabulary for the cascade layer (TGCLOUD-1712, W4.4).
//
// WHY THIS EXISTS
//
// Three disjoint, hand-maintained sets used to decide what a genotype result
// means, against a UI vocabulary of EIGHT values:
//
//   TESTED_CARRIER_VALUES  (carrier-status/constants.js) = carrier, heterozygote
//   CONFIRMED_STATUSES     (cascadeScreening.js)         = + non-carrier,
//                                                          homozygote-or-compound-
//                                                          heterozygote,
//                                                          homozygous-normal
//   NEGATIVE_STATUSES      (detectors/cascade.js)        = non-carrier,
//                                                          homozygous-normal
//
// `homozygote`, `hemizygote` and `normal` appeared in NONE of them. Consequences,
// all real: a hemizygous male — the canonical X-linked positive — created no
// cascade index, was never suppressed as already-tested and never pruned a
// transmission path; and a `normal` result counted as UNTESTED, so the software
// recommended a test to somebody who had already had one and been cleared.
//
// SOURCE OF TRUTH. The +/- direction comes from `CARRIER_STATUS_SYMBOL`
// (pedigree/carrierStatusMark.js), which is the value→meaning map the pedigree
// itself draws from and the closest thing this codebase has to a single source.
// A fourth hand-written list is exactly the defect, so this module DERIVES from
// that map: `+` → positive, `-` → negative. Adding a value there classifies it
// here automatically. `carrierStatusVocabulary.test.js` pins the derivation
// against the shipped dropdown list (CONSTANTS.carrierStatuses), so a new value
// added to the UI without a symbol fails loudly instead of silently landing in
// the `unrecognised` bucket.
//
// This module deliberately does NOT redefine TESTED_CARRIER_VALUES. That set
// decides whether to DRAW a gene→disease carrier mark — a rendered clinical
// statement about a condition, on a separate pipeline — and widening it would
// change the chart. This vocabulary answers a different question: what does this
// person's genotype result say about the cascade.

import { CARRIER_STATUS_SYMBOL } from '../pedigree/carrierStatusMark';
import { BUCKET, leadingClassification } from 'src/util/variantPathogenicity';

// What a single `carrierStatus` VALUE means.
export const CARRIER_RESULT = Object.freeze({
  positive: 'positive', // the variant was found
  negative: 'negative', // it was looked for and not found
  inconclusive: 'inconclusive', // a result exists but does not settle carrier state
  absent: 'absent', // no status recorded — not tested
  unrecognised: 'unrecognised', // a value outside our vocabulary (integration drift)
  phenotypeStatement: 'phenotype_statement' // 'Affected' — not a test result at all
});

// Recognised but deliberately markerless: 'Affected' is a phenotype assertion
// carried by diagnoses, not a genotype result (TGCLOUD-1647). It must never be
// read as evidence that the person has been tested.
const PHENOTYPE_STATEMENT_VALUES = Object.freeze(new Set(['Affected']));

// Absent spellings. 'None' is what the older dropdown wrote for "not recorded".
const ABSENT_VALUES = Object.freeze(new Set(['', 'none']));

/** Every carrier-status value this vocabulary understands. */
export const CARRIER_STATUS_VALUES = Object.freeze(
  Object.keys(CARRIER_STATUS_SYMBOL)
);

/**
 * Classify one raw `carrierStatus` value.
 * @param {string|null|undefined} value
 * @returns {string} a CARRIER_RESULT
 */
export function classifyCarrierStatus(value) {
  if (value == null) return CARRIER_RESULT.absent;
  const raw = String(value).trim();
  if (ABSENT_VALUES.has(raw.toLowerCase())) return CARRIER_RESULT.absent;
  if (PHENOTYPE_STATEMENT_VALUES.has(raw))
    return CARRIER_RESULT.phenotypeStatement;
  const symbol = CARRIER_STATUS_SYMBOL[raw];
  if (symbol === '+') return CARRIER_RESULT.positive;
  if (symbol === '-') return CARRIER_RESULT.negative;
  return CARRIER_RESULT.unrecognised;
}

export const isPositiveCarrierStatus = (value) =>
  classifyCarrierStatus(value) === CARRIER_RESULT.positive;

export const isNegativeCarrierStatus = (value) =>
  classifyCarrierStatus(value) === CARRIER_RESULT.negative;

// ---------------------------------------------------------------------------
// Per-gene RESULT: the carrier-status dropdown AND the variant record
// ---------------------------------------------------------------------------
//
// The structured dropdown defaults to empty, so keying "has this person been
// tested?" off it alone reports a person with a recorded PATHOGENIC variant as
// untested and recommends them their own test (TGCLOUD-1712 W4.3, the John
// Blythe defect). The variant record is the second channel and it is consulted
// here.

export const GENOTYPE_RESULT = Object.freeze({
  positive: 'positive',
  negative: 'negative',
  inconclusive: 'inconclusive',
  none: 'none'
});

export const RESULT_BASIS = Object.freeze({
  carrierStatus: 'carrier_status',
  variantInterpretation: 'variant_interpretation'
});

/** A definitive result — one that settles this person's carrier state. */
export const isDefinitiveResult = (result) =>
  result === GENOTYPE_RESULT.positive || result === GENOTYPE_RESULT.negative;

/** Bare gene symbol, upper-cased: 'BRCA1 (NCBIGene:672)' → 'BRCA1'. */
export const normaliseGene = (value) =>
  String(value || '')
    .split(' (')[0]
    .trim()
    .toUpperCase();

/** The gene a genotype record is about, whatever shape the record is in. */
export const geneOf = (g) => normaliseGene((g && (g.symbol || g.gene)) || '');

/**
 * A genotype's variant-level interpretation: 'pathogenic' | 'benign' | 'vus' |
 * 'unknown'.
 *
 * Tokenised individuals already carry `interpretation` (tokenise.js). RAW
 * members — what the toolbar hands `selectCascadeCandidates` — do not, so it is
 * derived here from the same shared primitive the tokeniser uses
 * (`leadingClassification`, clinician → laboratory → ClinVar). Deriving it from
 * the shared module rather than a private copy is the whole point: a fifth
 * classification rule is how the analyzer and the sidebar came to disagree once
 * already.
 */
export function genotypeInterpretation(g) {
  if (g && typeof g.interpretation === 'string' && g.interpretation)
    return g.interpretation;
  const variants = Array.isArray(g && g.variants) ? g.variants : [];
  let strongest = 'unknown';
  for (const v of variants) {
    const bucket = leadingClassification(v)?.bucket || null;
    if (bucket === BUCKET.pathogenic) return 'pathogenic';
    if (bucket === BUCKET.uncertain && strongest === 'unknown')
      strongest = 'vus';
    else if (bucket === BUCKET.benign && strongest === 'unknown')
      strongest = 'benign';
  }
  return strongest;
}

/**
 * What one genotype record says about this person's carrier state.
 *
 * Precedence and the conflict rule (clinical decisions, TGCLOUD-1712 W4.3):
 *
 *  - An explicit carrier status is the clinician's own assertion and leads.
 *  - carrierStatus NEGATIVE + a PATHOGENIC variant on the same gene is a
 *    contradiction, not a result: it degrades to `inconclusive` with
 *    `conflict: true`. Believing the negative would remove the person from the
 *    cascade on the strength of a self-contradicting record, and removal is the
 *    unsafe direction.
 *  - carrierStatus POSITIVE + a BENIGN variant stays positive (flagged): the
 *    person is treated as already-known-positive, which never under-serves them.
 *  - With no usable carrier status, the variant record decides — pathogenic →
 *    positive, benign → negative, VUS → inconclusive.
 *  - `unrecognised` and `phenotype_statement` values are NOT results. They fall
 *    through to the variant record and, failing that, to `none` (= untested),
 *    which keeps the person in the cascade.
 *
 * @returns {{ result: string, basis: string|null, gene: string,
 *             carrierStatus: string|null, interpretation: string,
 *             conflict: boolean, dateRecorded: string|null }}
 */
export function classifyGenotypeResult(g) {
  const carrierStatus = (g && g.carrierStatus) || null;
  const status = classifyCarrierStatus(carrierStatus);
  const interpretation = genotypeInterpretation(g);
  // `dateRecorded` is not currently carried through tokenise.js, so it is null
  // for tokenised individuals. Read defensively so it flows through the moment
  // the tokeniser passes it on — no change needed here. Nothing in the cascade
  // BRANCHES on it: see the W4.3 note in detectors/cascade.js.
  const dateRecorded = (g && g.dateRecorded) || null;
  const out = {
    result: GENOTYPE_RESULT.none,
    basis: null,
    gene: geneOf(g),
    carrierStatus,
    interpretation,
    conflict: false,
    dateRecorded
  };

  if (status === CARRIER_RESULT.positive) {
    return {
      ...out,
      result: GENOTYPE_RESULT.positive,
      basis: RESULT_BASIS.carrierStatus,
      conflict: interpretation === 'benign'
    };
  }
  if (status === CARRIER_RESULT.negative) {
    if (interpretation === 'pathogenic')
      return {
        ...out,
        result: GENOTYPE_RESULT.inconclusive,
        basis: RESULT_BASIS.carrierStatus,
        conflict: true
      };
    return {
      ...out,
      result: GENOTYPE_RESULT.negative,
      basis: RESULT_BASIS.carrierStatus
    };
  }

  if (interpretation === 'pathogenic')
    return {
      ...out,
      result: GENOTYPE_RESULT.positive,
      basis: RESULT_BASIS.variantInterpretation
    };
  if (interpretation === 'benign')
    return {
      ...out,
      result: GENOTYPE_RESULT.negative,
      basis: RESULT_BASIS.variantInterpretation
    };
  if (interpretation === 'vus')
    return {
      ...out,
      result: GENOTYPE_RESULT.inconclusive,
      basis: RESULT_BASIS.variantInterpretation
    };
  return out;
}

// Which of several results for the same gene represents the person. A positive
// leads (a carrier is always reported), then a negative, then an inconclusive —
// so a VUS never masks a definitive result sitting behind it in the array.
const RESULT_RANK = {
  [GENOTYPE_RESULT.positive]: 3,
  [GENOTYPE_RESULT.negative]: 2,
  [GENOTYPE_RESULT.inconclusive]: 1,
  [GENOTYPE_RESULT.none]: 0
};

/**
 * This person's result for `gene`, or null when they have none.
 *
 * With no `gene` this returns NULL, deliberately. Callers use a result to
 * suppress or prune, and a result for some unrelated gene says nothing about
 * this family's variant (TGCLOUD-1712 CT-3 — see cascadeScreening.js).
 *
 * @param {{ genotypes?: Array<object> }} member raw member OR tokenised individual
 * @param {string|null} gene
 */
export function resultForGene(member, gene) {
  const wanted = normaliseGene(gene);
  if (!wanted) return null;
  let best = null;
  for (const g of (member && member.genotypes) || []) {
    if (geneOf(g) !== wanted) continue;
    const r = classifyGenotypeResult(g);
    if (r.result === GENOTYPE_RESULT.none) continue;
    if (!best || RESULT_RANK[r.result] > RESULT_RANK[best.result]) best = r;
  }
  return best;
}

/** Any definitive (positive or negative) result for `gene`. */
export const hasDefinitiveResultForGene = (member, gene) => {
  const r = resultForGene(member, gene);
  return !!r && isDefinitiveResult(r.result);
};
